import React from "react";
import { Overline } from "../../../../ForBrands/ui/BrandUI";
import RankChip, { MedianChip } from "../../../../ForBrands/ui/RankChip";
import GameResultPill, { RESULT_WORDS } from "../../../../ForBrands/ui/ResultPill";
import { usePhoneTheme } from "../../../../ForBrands/useIsPhone";
import { EMPTY, toNum } from "../../../../../utils/perfFormat";

// Small pieces shared by the Team > Performance cards (BUILD_SPEC B7):
// the stat tile with its rank chip, its loading twin, a big figure with an
// eyebrow, the W / L pill, the Edge / Deficit tag and the lines of a
// hover or tap detail.

const cx = (...parts) => parts.filter(Boolean).join(" ");

// The passive "Med X" chip (B5) with a ready-made text (a height in feet
// and inches, a signed net): the MedianChip the athlete cards use.
export const MedianText = ({ text }) => <MedianChip text={text} />;

// A stat tile (16px corners): the value, the label as an overline, the
// rank chip (B5: "Top N%", "Not ranked", a passive "46th pct" for a
// descriptive metric, ≈ for an approximate norm, the band dot when `band`)
// and the peer median when the payload has one.
export const PerfTile = ({ tile, band = false }) => (
  <div className="flex min-w-0 flex-col items-center gap-2 rounded-[16px] border border-bm-border px-2 py-4 text-center">
    <span className="bm-num-ml tabular-nums text-bm-ink">{tile.value}</span>
    <Overline className="break-words">{tile.label}</Overline>
    {(tile.chip || tile.median) && (
      <span className="flex flex-wrap items-center justify-center gap-[6px]">
        {tile.chip && (
          <RankChip percentile={tile.chip.percentile} kind={tile.chip.kind} approximate={tile.chip.approximate} band={band} label={tile.chip.label} />
        )}
        <MedianText text={tile.median} />
      </span>
    )}
  </div>
);

// The tile while its numbers load (no text, so nothing is read out).
export const PendingTile = () => (
  <div aria-hidden="true" className="flex min-w-0 flex-col items-center gap-2 rounded-[16px] border border-bm-border px-2 py-4">
    <span className="h-[26px] w-14 animate-pulse rounded-[6px] bg-bm-mutedbg" />
    <span className="h-[10px] w-16 animate-pulse rounded-full bg-bm-mutedbg" />
    <span className="h-[22px] w-[72px] animate-pulse rounded-full bg-bm-mutedbg" />
  </div>
);

// The tile grid: two columns on phones (10px apart), up to `columns` on
// wide screens.
const DESKTOP_GRIDS = {
  4: "grid grid-cols-2 gap-3 sm:grid-cols-4",
  5: "grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5",
};
export const TileGrid = ({ columns = 4, children, label }) => {
  const phone = usePhoneTheme();
  return (
    <div role={label ? "group" : undefined} aria-label={label || undefined} className={phone ? "grid grid-cols-2 gap-[10px]" : DESKTOP_GRIDS[columns] || DESKTOP_GRIDS[4]}>
      {children}
    </div>
  );
};

// A headline figure: the eyebrow over the number; `toneValue` (a number)
// colours it bm-positive at or above zero and bm-critical below (Current
// form, Season Net).
export const Figure = ({ eyebrow, value, toneValue, align = "left", chip, note }) => {
  const n = toNum(toneValue);
  const color = n === null ? "text-bm-ink" : n >= 0 ? "text-bm-positive" : "text-bm-critical";
  return (
    <div className={cx("flex min-w-0 flex-col gap-1", align === "right" ? "items-end text-right" : "items-start")}>
      <Overline>{eyebrow}</Overline>
      <span className="flex flex-wrap items-center gap-2">
        <span className={cx("bm-num-l tabular-nums", color)}>{value ?? EMPTY}</span>
        {chip}
      </span>
      {note && <span className="bm-body-xs text-bm-muted">{note}</span>}
    </div>
  );
};

// W / L / T pill (B18: a team win in bm-orange, a loss grey), the shared
// ResultPill the athlete's Game by Game uses too; an en dash for a game
// with no final result.
export const ResultPill = ({ result }) =>
  RESULT_WORDS[result] ? <GameResultPill result={result} /> : <span className="bm-body-m text-bm-tertiary">{EMPTY}</span>;

// The four factors' Edge (bm-positive) or Deficit (bm-critical); nothing
// when the payload cannot tell.
export const EdgeTag = ({ edge }) => {
  if (edge === "edge") return <span className="shrink-0 bm-label-xs text-bm-positive">Edge</span>;
  if (edge === "deficit") return <span className="shrink-0 bm-label-xs text-bm-critical">Deficit</span>;
  return null;
};

// The body of a chart's hover card (desktop) or tap panel (phone): a bold
// title, an optional sub line, then label / value rows.
export const DetailLines = ({ title, sub, rows = [] }) => (
  <span className="flex flex-col gap-1">
    <span className="bm-label-l text-bm-ink">{title}</span>
    {sub && <span className="bm-body-s text-bm-muted">{sub}</span>}
    {rows
      .filter((r) => r && r[1] !== null && r[1] !== undefined)
      .map(([label, value]) => (
        <span key={label} className="flex items-baseline justify-between gap-3 bm-body-s">
          <span className="text-bm-muted">{label}</span>
          <span className="font-archivo font-bold tabular-nums text-bm-ink">{value}</span>
        </span>
      ))}
  </span>
);

// A note under a card's figures (captions, footnotes).
export const CardNote = ({ children }) => (children ? <p className="break-words bm-body-s text-bm-muted">{children}</p> : null);
